import {cumulativeSum} from '@benchling/js-utils';
import assert from 'assert';
import $ from 'jquery';
import React, {CSSProperties, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'underscore';

import {sum} from 'BenchlingUtil';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {TABLE_ROW_HEIGHT} from 'notebook/tables/TableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';

/**
 * Thickness in px of the line indicating the new position of the dragged rows/columns.
 * @package exported for testing
 */
export const DRAGGER_LINE_THICKNESS = 3;

export interface DraggableDimensionMoverProps {
  /**
   * Reference to the <table> element.
   */
  tableRef: React.RefObject<Element | null>;
  /**
   * The dimension being reordered (row or column).
   */
  dimension: TableDimension;
  /**
   * The index of the first row/column being dragged.
   */
  startIndex: number;
  /**
   * The index of the last row/column being dragged, inclusive.
   */
  endIndex: number;
  /**
   * The index of the first visible item in the table. (ex: there are Single Input fields on the table)
   * This should be the index of the first item represented in the columnWidths/rowHeights
   * Default is 0.
   */
  getFirstVisibleIndex: () => number;
  /**
   * Callback to be called when the user releases their mouse.
   * insertIndex is the index where the rows/columns should be inserted.
   */
  onDragComplete: (insertIndex: number) => void;
  /**
   * Width of the row index column.
   */
  firstColumnWidth: number;
  /**
   * Widths of the actual rendered columns, not including the row index column or the single inputs
   */
  columnWidths: Array<number>;
  /**
   * Height of the header row.
   */
  headerRowHeight: number;
  /**
   * Heights of the actual rendered rows, not including the header row, the new row dragger, or any
   * virtualized rows.
   */
  rowHeights: Array<number>;
  /**
   * Any vertical offset we want to apply. For instance, the Single Input group section.
   */
  verticalOffset: number;
}

/**
 * Finds the insertion index of the given range if the user was to release their mouse.
 *
 * @package exported for testing
 *
 * @param runningSums Cumulative sum of the pixel sizes of the dimension being reordered.
 * @param dragPosition Pixel position of the user's mouse.
 * @param startIndex Starting index of the dragged range, inclusive.
 * @param endIndex Ending index of the dragged rance, inclusive.
 * @returns The position to insert the dragged range if the user was to release their mouse at dragPosition.
 */
export function _findInsertIndex(
  runningSums: Array<number>,
  dragPosition: number,
  startIndex: number,
  endIndex: number
): number {
  let insertIndex = _.sortedIndex(runningSums, dragPosition);
  // Avoid reordering to the same position
  if (
    // insertIndex is inside of the selected range
    (insertIndex > startIndex && insertIndex <= endIndex) ||
    // insertIndex is after the end of the selected range, but the selected range is already at the end
    (insertIndex > endIndex && endIndex === runningSums.length - 1)
  ) {
    insertIndex = startIndex;
  } else if (insertIndex > endIndex) {
    insertIndex = Math.min(insertIndex + 1, runningSums.length);
  }
  return insertIndex;
}

/**
 * A React element showing a shadow of the dragged rows/columns and a line indicating the new position.
 * Should be rendered in <TableEditable> for drag-and-drop reordering after the user has moused down.
 *
 * This calculates the position of the shadow and line based on the mouse position and the sizes of the
 * rows/columns.  The mouse's x/y is compared to the x/y position of the tableRef to determine the position
 * relative to the table.
 *
 * That relative position is then used with the rowHeights/columnHeights, as well as the
 * firstColumnWidth/headerRowHeight and verticalOffset, to determine which row/column the user is hovering
 * over.
 *
 */
export default function DraggableDimensionMover({
  dimension,
  startIndex,
  endIndex,
  getFirstVisibleIndex = () => 0,
  tableRef,
  firstColumnWidth,
  headerRowHeight,
  verticalOffset,
  rowHeights,
  columnWidths,
  onDragComplete,
}: DraggableDimensionMoverProps): JSX.Element {
  assert(tableRef.current != null, 'Expected to have a tableRef');

  const insertIndexRef = useRef<number>(startIndex);
  const [mousePosition, setMousePosition] = useState<number | null>(null);

  useEffect(() => {
    function handleMouseMove(event: MouseEvent): void {
      if (dimension === TableDimension.ROW) {
        setMousePosition(event.clientY);
      } else {
        setMousePosition(event.clientX);
      }
    }
    function handleMouseUp(): void {
      onDragComplete(insertIndexRef.current);
    }
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp, {once: true});
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dimension, onDragComplete]);

  // Sizes of the dimension being reordered
  const dimensionSizes = dimension === TableDimension.ROW ? rowHeights : columnWidths;
  // runningSums[n] is the bottom position of the nth row, or the right position of the nth column
  const runningSums: Array<number> = useMemo(() => cumulativeSum(dimensionSizes), [dimensionSizes]);
  const firstVisibleIndex = getFirstVisibleIndex();
  const renderStartIndex = startIndex - firstVisibleIndex;
  const renderEndIndex = endIndex - firstVisibleIndex;

  const shadowSizeRef = useRef<number>(TABLE_ROW_HEIGHT);
  const shadowSize = useMemo(() => {
    // If the elements being dragged get virtualized out of view, we need to remember how large the shadow is
    // So update the ref while the items are in view;
    if (renderStartIndex >= 0 && renderEndIndex < dimensionSizes.length) {
      shadowSizeRef.current = sum(dimensionSizes.slice(renderStartIndex, renderEndIndex + 1));
    }
    return shadowSizeRef.current;
  }, [renderStartIndex, renderEndIndex, dimensionSizes]);

  const mouseScrollPosition = useMemo(() => {
    assert(tableRef.current != null, 'Expected to have a tableRef');
    const tablePosition =
      (dimension === TableDimension.ROW
        ? tableRef.current.getBoundingClientRect().y
        : tableRef.current.getBoundingClientRect().x) ?? 0;
    // If the mouse hasn't moved yet, then we don't know where it is
    // But we want the shadow to start mirroring the position of the dragged items
    if (mousePosition == null) {
      const previousItems = renderStartIndex > 0 ? runningSums[renderStartIndex - 1] : 0;
      if (dimension === TableDimension.ROW) {
        return headerRowHeight + previousItems + shadowSize / 2;
      } else {
        return firstColumnWidth + previousItems + shadowSize / 2;
      }
    }
    return mousePosition - tablePosition;
  }, [
    mousePosition,
    dimension,
    tableRef,
    shadowSize,
    runningSums,
    renderStartIndex,
    firstColumnWidth,
    headerRowHeight,
  ]);

  const mousePositionExcludingOffsets =
    mouseScrollPosition - (dimension === TableDimension.ROW ? headerRowHeight : firstColumnWidth);

  const renderInsertIndex = _findInsertIndex(
    runningSums,
    mousePositionExcludingOffsets,
    renderStartIndex,
    renderEndIndex
  );

  insertIndexRef.current = renderInsertIndex + firstVisibleIndex;

  const tableWidth = $(tableRef.current).width();
  const tableHeight = $(tableRef.current).height();

  let shadowStyles: CSSProperties;
  let lineStyles: CSSProperties;

  if (dimension === TableDimension.ROW) {
    shadowStyles = {
      top: mouseScrollPosition + verticalOffset - shadowSize / 2,
      maxWidth: 'none',
      height: shadowSize,
      left: 0,
    };
    lineStyles = {
      top:
        headerRowHeight +
        verticalOffset +
        sum(dimensionSizes.slice(0, renderInsertIndex)) -
        DRAGGER_LINE_THICKNESS / 2,
      left: 0,
      height: DRAGGER_LINE_THICKNESS,
    };
  } else {
    const shadowHeight = headerRowHeight + sum(rowHeights);
    shadowStyles = {
      top: verticalOffset,
      left: mouseScrollPosition - shadowSize / 2,
      maxWidth: shadowSize,
      height: shadowHeight,
    };
    lineStyles = {
      top: verticalOffset,
      left: firstColumnWidth + sum(dimensionSizes.slice(0, renderInsertIndex)) - DRAGGER_LINE_THICKNESS / 2,
      width: DRAGGER_LINE_THICKNESS,
      height: shadowHeight,
    };
  }

  return (
    <div
      className="draggableDimensionMover"
      {...getTestProps({component: 'DraggableDimensionMover', element: 'root'})}
    >
      <div
        className="draggableDimensionMover-infoOverlay"
        style={{
          width: tableWidth,
          height: tableHeight,
          top: 0,
          left: 0,
        }}
      >
        <div className="draggableDimensionMover-infoOverlay-selectedCellsShadow" style={shadowStyles} />
        <div className="draggableDimensionMover-infoOverlay-newPositionLine" style={lineStyles} />
      </div>
    </div>
  );
}
